import { useEditor } from '@craftjs/core';
import { useCallback, useRef, useState } from 'react';

import { CommonData } from '@/components/blockEditor/blockEditor.types';
import { transformCraftToCommonData } from '@/components/blockEditor/transformCraftToCommonData';
import { Button } from '@/components/form/baseInputs/Button';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { getUserFacingErrorMessages } from '@/graphql/errors';
import { useReportError } from '@/hooks/useReportError';
import { TenantSlideConfigConfigType } from '@/types/schema';

import {
  EducationListItemType,
  PLANNING_BASICS_CATEGORY,
} from '../../Education.constants';
import { useEducationSlideOverride } from '../../EducationSlideOverrides/EducationSlideOverrides.context';
import { getEducationSlideDefaultData } from '../EducationSlideDefaultData';
import {
  Operations,
  useCreateTenantSlideConfigMutation,
  useUpdateTenantSlideConfigMutation,
} from '../graphql/EducationSlideTrowser.generated';
import {
  DRAFT_SLIDE_ID,
  generateCustomSlideIdentifier,
  getSlideDisplayName,
  getSlideIdentifierFromEducationSlideId,
} from '../utils/slideIdentifier.utils';

/** Default category for custom slides when none is specified */
const DEFAULT_CUSTOM_SLIDE_CATEGORY = PLANNING_BASICS_CATEGORY;
/** Default display name for custom slides when none is specified */
const DEFAULT_CUSTOM_SLIDE_TITLE = 'Custom page';

export interface SaveButtonProps {
  educationSlideId: EducationListItemType;
  currentSlideIndex: number;
  /** Existing config ID if one exists for this slide/page */
  existingConfigId?: string;
  onSaveSuccess?: (configId: string) => void;
  /** Called when a new custom slide is created from a draft */
  onSlideCreated?: (slideIdentifier: string) => void;
  /** Whether this is a custom page (draft or existing custom slide) */
  isCustomPage?: boolean;
  /** Education category for custom slides */
  customSlideEducationCategory?: string;
  /** Custom slide title (display name) */
  customSlideTitle?: string;
  /** Slide identifier of the source slide this was duplicated from (persisted through saves) */
  duplicatedFromSlideId?: string;
  /** Template slide title override (display name) */
  templateSlideTitle?: string;
  /**
   * Config ID for page 0 (used for title storage).
   * Template slide titles are always stored on page 0's config for consistency
   * with how useEducationMap() reads titles.
   */
  page0ConfigId?: string;
}

export function SaveButton({
  educationSlideId,
  currentSlideIndex,
  existingConfigId,
  onSaveSuccess,
  onSlideCreated,
  isCustomPage = false,
  customSlideEducationCategory,
  customSlideTitle,
  duplicatedFromSlideId,
  templateSlideTitle,
  page0ConfigId,
}: SaveButtonProps) {
  const { query } = useEditor();
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const override = useEducationSlideOverride(
    isCustomPage
      ? null
      : getSlideIdentifierFromEducationSlideId(
          educationSlideId,
          currentSlideIndex
        )
  );
  const educationSlideOptions = override?.contentJSON.educationSlideOptions;

  const isDraft = educationSlideId === DRAFT_SLIDE_ID;

  // Notify the parent about a new draft only after the save completes.
  const createdSlideIdentifierRef = useRef<string | null>(null);

  // The title mutation must not replace the current page's saved config ID.
  const isSilentMutationRef = useRef(false);
  const savedConfigIdRef = useRef<string | null>(null);
  const saveFailedRef = useRef(false);
  const savingRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);

  const [createMutation, { client }] = useCreateTenantSlideConfigMutation({
    refetchQueries: [Operations.Query.GetEducationPageEditor],
    awaitRefetchQueries: true,
    onError: (error) => {
      saveFailedRef.current = true;
      const messages = getUserFacingErrorMessages(error);
      showFeedback(messages.join(' ') || FeedbackMessages.formSaveError, {
        variant: 'error',
      });
      if (
        error.networkError ||
        messages.length === 0 ||
        messages.length !== error.graphQLErrors.length
      ) {
        reportError('Failed to create education slide config', error);
      }
      createdSlideIdentifierRef.current = null;
      isSilentMutationRef.current = false;
    },
    onCompleted: (data) => {
      if (!isSilentMutationRef.current) {
        savedConfigIdRef.current = data.createTenantSlideConfig.id;
      }
      isSilentMutationRef.current = false;
    },
  });

  const [updateMutation] = useUpdateTenantSlideConfigMutation({
    refetchQueries: [Operations.Query.GetEducationPageEditor],
    awaitRefetchQueries: true,
    onError: (error) => {
      saveFailedRef.current = true;
      showFeedback(FeedbackMessages.formSaveError, { variant: 'error' });
      reportError('Failed to update education slide config', error);
      isSilentMutationRef.current = false;
    },
    onCompleted: (data) => {
      if (!isSilentMutationRef.current) {
        savedConfigIdRef.current = data.updateTenantSlideConfig.id;
      }
      isSilentMutationRef.current = false;
    },
  });

  /**
   * Save a custom page (draft or existing custom slide).
   * Issues exactly 1 mutation (create or update).
   */
  const saveCustomPageConfig = useCallback(
    async (contentJSON: CommonData) => {
      const customSlideContentJSON = {
        ...contentJSON,
        educationCategory:
          customSlideEducationCategory || DEFAULT_CUSTOM_SLIDE_CATEGORY,
        ...(duplicatedFromSlideId
          ? { duplicatedFrom: duplicatedFromSlideId }
          : {}),
      };

      if (existingConfigId) {
        // Update existing custom slide
        await updateMutation({
          variables: {
            input: {
              id: existingConfigId,
              update: {
                contentJSON: customSlideContentJSON,
                ...(customSlideTitle ? { displayName: customSlideTitle } : {}),
              },
            },
          },
        });
      } else {
        // Create new custom slide config.
        // For drafts, generate a new identifier. For existing custom slides without
        // a config (edge case - e.g., config was deleted), use the existing identifier.
        const slideIdentifier = isDraft
          ? generateCustomSlideIdentifier()
          : educationSlideId;

        if (isDraft) {
          createdSlideIdentifierRef.current = slideIdentifier;
        }

        await createMutation({
          variables: {
            input: {
              create: {
                configType: TenantSlideConfigConfigType.CustomSlideTemplate,
                slideIdentifier,
                contentJSON: customSlideContentJSON,
                displayName: customSlideTitle || DEFAULT_CUSTOM_SLIDE_TITLE,
              },
            },
          },
        });
      }
    },
    [
      existingConfigId,
      isDraft,
      educationSlideId,
      customSlideEducationCategory,
      customSlideTitle,
      duplicatedFromSlideId,
      createMutation,
      updateMutation,
    ]
  );

  /**
   * Save a template slide override.
   * Issues 1 mutation for the current page, plus optionally 1 more to sync the title to page 0.
   *
   * Template slide titles are always stored on page 0's config for consistency
   * with how useEducationMap() reads titles. When editing from a non-zero page,
   * we need a separate mutation to update/create the page 0 config with the title.
   */
  const saveTemplatePageConfig = useCallback(
    async (contentJSON: CommonData) => {
      const isOnPage0 = currentSlideIndex === 0;

      // Save the current page's content
      if (existingConfigId) {
        // Update existing template override
        // Only save displayName on page 0 (other pages store title on page 0's config)
        const displayNameUpdate =
          isOnPage0 && templateSlideTitle ? templateSlideTitle : undefined;

        await updateMutation({
          variables: {
            input: {
              id: existingConfigId,
              update: {
                contentJSON,
                ...(displayNameUpdate
                  ? { displayName: displayNameUpdate }
                  : {}),
              },
            },
          },
        });
      } else {
        // Create new template override
        const slideIdentifier = getSlideIdentifierFromEducationSlideId(
          educationSlideId,
          currentSlideIndex
        );
        // Use custom title only on page 0; other pages use the default display name
        const defaultDisplayName = getSlideDisplayName(
          educationSlideId,
          currentSlideIndex
        );
        const displayName =
          isOnPage0 && templateSlideTitle
            ? templateSlideTitle
            : defaultDisplayName;

        await createMutation({
          variables: {
            input: {
              create: {
                configType: TenantSlideConfigConfigType.EducationSlideOverride,
                slideIdentifier,
                contentJSON,
                displayName,
              },
            },
          },
        });
      }

      // Apollo resolves mutation errors when onError is provided. Stop after failure.
      if (saveFailedRef.current) return;

      // If editing from a non-zero page with a title change, sync title to page 0
      if (!isOnPage0 && templateSlideTitle) {
        isSilentMutationRef.current = true;

        if (page0ConfigId) {
          await updateMutation({
            variables: {
              input: {
                id: page0ConfigId,
                update: { displayName: templateSlideTitle },
              },
            },
          });
        } else {
          const page0Identifier = getSlideIdentifierFromEducationSlideId(
            educationSlideId,
            0
          );
          const page0DefaultData = getEducationSlideDefaultData(
            educationSlideId,
            0
          );
          await createMutation({
            variables: {
              input: {
                create: {
                  configType:
                    TenantSlideConfigConfigType.EducationSlideOverride,
                  slideIdentifier: page0Identifier,
                  contentJSON: page0DefaultData,
                  displayName: templateSlideTitle,
                },
              },
            },
          });
        }
      }
    },
    [
      currentSlideIndex,
      existingConfigId,
      educationSlideId,
      templateSlideTitle,
      page0ConfigId,
      createMutation,
      updateMutation,
    ]
  );

  const handleSave = useCallback(async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setIsSaving(true);
    saveFailedRef.current = false;
    savedConfigIdRef.current = null;
    try {
      // Get Craft.js serialized data and transform to CommonData format
      const craftJson = query.serialize();
      const craftNodes = JSON.parse(craftJson);
      const contentJSON = transformCraftToCommonData(craftNodes);
      // Craft edits content blocks, not the tenant's built-in slide layout.
      if (educationSlideOptions) {
        contentJSON.educationSlideOptions = educationSlideOptions;
      }

      if (isCustomPage) {
        await saveCustomPageConfig(contentJSON);
      } else {
        await saveTemplatePageConfig(contentJSON);
      }
      // Keep the editor open until both content and title are saved.
      if (saveFailedRef.current) {
        // Refresh mutation IDs so reopening the editor does not reuse stale rows.
        await client.refetchQueries({
          include: [Operations.Query.GetEducationPageEditor],
        });
        return;
      }
      if (!savedConfigIdRef.current) return;
      showFeedback(
        isCustomPage
          ? 'Custom page saved successfully'
          : 'Slide customization saved successfully',
        { variant: 'success' }
      );
      onSaveSuccess?.(savedConfigIdRef.current);
      if (createdSlideIdentifierRef.current) {
        onSlideCreated?.(createdSlideIdentifierRef.current);
        createdSlideIdentifierRef.current = null;
      }
    } catch (error) {
      showFeedback(FeedbackMessages.formSaveError, { variant: 'error' });
      reportError(
        'Failed to save education slide config',
        error instanceof Error ? error : new Error(String(error))
      );
    } finally {
      savingRef.current = false;
      setIsSaving(false);
      isSilentMutationRef.current = false;
    }
  }, [
    query,
    educationSlideOptions,
    isCustomPage,
    saveCustomPageConfig,
    saveTemplatePageConfig,
    showFeedback,
    onSaveSuccess,
    onSlideCreated,
    reportError,
    client,
  ]);

  return (
    <Button
      variant="primary"
      size="sm"
      onClick={handleSave}
      loading={isSaving}
      disabled={isSaving}
    >
      Done
    </Button>
  );
}
